import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Box, Card, Typography } from "@mui/material";
import ResourceList from "src/components/shared/ResourceList";
import { contentApi, rows } from "src/pages/website-content/api";
import { PostFunction } from "src/utils/axios_connection";
import UrlApi from "src/utils/Url";
import { GetStats } from "src/views/dashboard/actions";
import { testWords } from "src/views/dashboard/numbers";
import { readHideTest } from "src/pages/cases/workspace";
import { figureWarning, liveCountFor, vagueLabel } from "./figures";
import { isOn, placeholderWarnings, rights } from "src/pages/website-content/checks";
import {
  CheckCell,
  ConfirmDialog,
  ContentPage,
  EditorDialog,
  Field,
  HonestyCheck,
  SaveSwitch,
  useConfirm,
  useContentList,
  useForm,
} from "src/pages/website-content/kit";

// Key numbers: the figures about Safrati ("200+ approvals"). In the data the
// number is `description` and what it counts is `title`; the old home page
// showed the number big with the label under it. No page of the current
// website shows them. Only numbers we can prove belong here: each is checked
// against Safrati's own records (the counts Home shows), and a number above
// them is flagged. Nothing here changes a number.

const EMPTY = { id: null, value: "", label: "", proven: false };

const VAGUE = "Say what it counts, like “Visas approved”.";

// What the check says about one number: the site-wide placeholder checks,
// a number above Safrati's records, and a label too short to say what it
// counts.
const warningsFor = (item, live) =>
  [...placeholderWarnings(item.title, item.description), figureWarning(item, live), vagueLabel(item.title) && VAGUE].filter(Boolean);

const nameOf = (item) => `${item.description || ""} ${item.title || ""}`.trim() || `number #${item.id}`;

// The tile as the website drew it: the number big, what it counts underneath.
function NumberTile({ value, label }) {
  return (
    <Box sx={{ textAlign: "center", py: 1.5 }}>
      <Typography
        sx={{
          fontSize: "2rem",
          lineHeight: 1.15,
          fontWeight: 700,
          color: "primary.main",
          fontVariantNumeric: "tabular-nums",
          overflowWrap: "anywhere",
        }}
      >
        {value || "—"}
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 0.5, overflowWrap: "anywhere" }}>
        {label || "What it counts"}
      </Typography>
    </Box>
  );
}

NumberTile.propTypes = { value: PropTypes.string, label: PropTypes.string };

const KeyNumbers = () => {
  const dispatch = useDispatch();
  const can = rights("17");
  // The list says a failed load itself, with Try again, so no toast too.
  const load = useCallback(
    () => PostFunction({ Route: UrlApi.stats.get_table, data: {}, isMultipart: false, showalert: false, showerror: false, dispatch }).then(rows),
    [dispatch]
  );
  const list = useContentList(load);
  const [editing, setEditing] = useState(null);
  const form = useForm(EMPTY);
  const confirm = useConfirm();
  const items = useMemo(() => list.items || [], [list.items]);
  // Safrati's own counts (all time), to check the numbers against: the
  // ones Home shows, with the same test-account switch as Home and Cases.
  // Null until they load, or if they can't: then only the other checks run.
  const [live, setLive] = useState(null);
  const [hideTest] = useState(readHideTest);
  useEffect(() => {
    dispatch(GetStats({ hideTest, callback: (res) => setLive(res?.data || null) }));
  }, [dispatch, hideTest]);
  const liveWords = live ? (live.hide_test === undefined ? "test accounts included" : testWords(!!live.hide_test)) : "";
  const liveCount = liveCountFor(form.values.label, live);

  const setActive = async (item, next) => {
    try {
      await contentApi.stats.set(item.id, "is_active", next ? 1 : 0, dispatch);
      list.patch(item.id, { is_active: next ? 1 : 0 });
      toast.success(next ? "Number switched on." : "Number switched off.");
    } catch {
      /* PostFunction said what went wrong; the switch stays */
    }
  };

  const openEditor = (item) => {
    form.reset(item ? { id: item.id, value: item.description || "", label: item.title || "", proven: false } : EMPTY);
    setEditing(item ? { item } : { item: null });
  };

  const save = async () => {
    const v = form.values;
    const errors = {};
    if (!v.value.trim()) errors.value = "Write the number as it should read, like 1,200+.";
    else if (!/\d/.test(v.value)) errors.value = "Use digits, like 1,200+ or 10.";
    if (!v.label.trim()) errors.label = "Say what the number counts, like Visas approved.";
    if (!v.proven) errors.proven = "Tick this to confirm. If you can't, don't save the number.";
    form.setErrors(errors);
    if (Object.keys(errors).length) return false;
    const body = { title: v.label.trim(), description: v.value.trim() };
    try {
      if (editing.item) {
        await contentApi.stats.edit({ id: v.id, ...body }, dispatch);
        toast.success("Number saved.");
      } else {
        await contentApi.stats.add(body, dispatch);
        toast.success("Number added.");
      }
      setEditing(null);
      list.reload();
      return true;
    } catch {
      return undefined;
    }
  };

  return (
    <ContentPage
      title="Key numbers"
      intro="Figures about Safrati, like visas approved, as number tiles."
      where={
        live
          ? `The website doesn’t show these numbers yet. Only keep numbers Safrati can prove from its own records: today ${liveCountFor("customers", live)?.value ?? 0} customer accounts, ${liveCountFor("approved", live)?.value ?? 0} approved applications and ${liveCountFor("support", live)?.value ?? 0} support chats, counted as on Home (one per application), ${liveWords} (the switch on Home and Cases).`
          : "The website doesn’t show these numbers yet. Only keep numbers Safrati can prove from its own records."
      }
      canAdd={can.write}
      addLabel="Add a number"
      onAdd={() => openEditor(null)}
      dialogs={
        <>
          <EditorDialog
            open={!!editing}
            title={editing?.item ? "Edit number" : "Add a number"}
            subtitle="No page of the current website shows these numbers. Your changes are kept for when one does."
            onClose={() => setEditing(null)}
            onSave={save}
            saveLabel={editing?.item ? "Save number" : "Add number"}
            errors={form.errors}
            dirty={form.dirty}
          >
            <Field
              label="Number"
              value={form.values.value}
              onChange={(v) => form.set("value", v)}
              error={form.errors.value}
              hint={
                figureWarning({ description: form.values.value, title: form.values.label }, live)
                  ? `${figureWarning({ description: form.values.value, title: form.values.label }, live)}. Use a number the records show.`
                  : liveCount
                  ? `As it should read, like 1,200+. Safrati’s records show ${liveCount.value.toLocaleString("en-US")} ${liveCount.words}.`
                  : "As it should read, like 1,200+ or 10."
              }
              counter={12}
            />
            <Field
              label="What it counts"
              value={form.values.label}
              onChange={(v) => form.set("label", v)}
              error={form.errors.label}
              hint="Say exactly what is counted, like “Visas approved” or “Customer accounts”, not one word like “Approval”."
              counter={30}
            />
            <Box>
              <Typography fontWeight={600} sx={{ mb: 0.75 }}>
                Preview
              </Typography>
              <Card variant="outlined" sx={{ maxWidth: 260 }}>
                <NumberTile value={form.values.value} label={form.values.label} />
              </Card>
            </Box>
            <HonestyCheck checked={form.values.proven} onChange={(value) => form.set("proven", value)} error={form.errors.proven}>
              This number is true today and Safrati can show where it comes from, for example its own application records.
            </HonestyCheck>
          </EditorDialog>

          <ConfirmDialog
            open={confirm.open}
            title="Delete this number?"
            confirmLabel="Delete number"
            onClose={confirm.close}
            onConfirm={async () => {
              try {
                await contentApi.stats.remove(confirm.item.id, dispatch);
                toast.success("Number deleted.");
                list.reload();
                return true;
              } catch {
                return false;
              }
            }}
          >
            “{confirm.item?.description} {confirm.item?.title}” is deleted. To keep it, switch off “Active” instead.
          </ConfirmDialog>
        </>
      }
    >
      <ResourceList
        label="Key numbers"
        items={items}
        primaryLabel="What it counts"
        primary={(item) => {
          // On a phone the Check column is hidden, so a number above the
          // records says so under its label.
          const over = figureWarning(item, live);
          return {
            title: item.title || "No label",
            subtitle: over ? (
              <Typography component="span" variant="body2" sx={{ display: { xs: "block", md: "none" }, color: "warning.dark", fontWeight: 600 }}>
                {over}
              </Typography>
            ) : undefined,
          };
        }}
        columns={[
          {
            key: "figure",
            label: "Number",
            width: 140,
            render: (item) => (
              <Typography component="span" sx={{ fontSize: "1.25rem", fontWeight: 700, color: "primary.main", fontVariantNumeric: "tabular-nums" }}>
                {item.description || "—"}
              </Typography>
            ),
          },
          {
            key: "active",
            // The glossary's words: shown on the website is "Live" (hala-09).
            label: "Live",
            width: 150,
            render: (item) => (
              <SaveSwitch
                label={isOn(item.is_active) ? "Live" : "Off"}
                item={nameOf(item)}
                checked={isOn(item.is_active)}
                disabled={!can.edit}
                onChange={(next) => setActive(item, next)}
              />
            ),
          },
          {
            key: "check",
            label: "Needs a look",
            hideBelow: "md",
            render: (item) => <CheckCell warnings={warningsFor(item, live)} />,
          },
        ]}
        onOpen={can.edit ? openEditor : undefined}
        openLabel={(item) => `Edit “${nameOf(item)}”`}
        rowActions={(item) => [
          can.edit && { label: "Edit", onClick: () => openEditor(item) },
          can.delete && { label: "Delete", destructive: true, onClick: () => confirm.ask(item) },
        ]}
        loading={list.loading}
        error={list.failed}
        onRetry={list.reload}
        empty={{ title: "No numbers yet." }}
      />
    </ContentPage>
  );
};

export default KeyNumbers;
